/**
 * Links into the URL scanner for a target URL.
 *
 * The obvious `/scan?url=<encoded>` form is BLOCKED AT THE EDGE and never reaches the SPA: the
 * CloudFront WAF runs AWSManagedRulesCommonRuleSet, whose `GenericRFI_QUERYARGUMENTS` rule 403s any
 * query argument containing `scheme://<IPv4-literal>`. So `http://example.com/a.pdf` loaded fine
 * while `http://203.26.79.13/file.pdf` returned a CloudFront 403 error page - which looked like the
 * link was simply broken. Only the *_BODY variants of that rule group are excluded on this ACL, so
 * POST bodies are unaffected and only GET query args need to avoid the pattern.
 *
 * Base64url keeps the target out of the pattern the rule matches, at the cost of an opaque link.
 * The legacy `url=` parameter is still READ (old links and bookmarks keep working for the URLs it
 * could carry); it is just never WRITTEN any more.
 */

function base64UrlEncode(value: string): string {
  const bytes = new TextEncoder().encode(value);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64UrlDecode(value: string): string {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4));
  return new TextDecoder().decode(Uint8Array.from(binary, (c) => c.charCodeAt(0)));
}

/** Link that opens the scanner for `url`, safe to put in an href / open in a new tab. */
export function scanLinkFor(url: string): string {
  return `/scan?u=${encodeURIComponent(base64UrlEncode(url))}`;
}

/**
 * The canonical, indexable address of a completed scan: `/scan/<hostname>/<uuid>`.
 *
 * WHY THE HOSTNAME IS IN THE PATH. Every scan used to live at `/scan?uuid=<uuid>`, a query string
 * that carries no keyword at all - the one word a person would search for, the hostname, appeared
 * nowhere in the URL. Worse, the SPA hardcoded `canonicalPath="/scan"`, so ~36,000 distinct results
 * all declared the bare scanner page as their canonical.
 *
 * THE HOST SEGMENT IS A SLUG, NOT AN IDENTIFIER. The uuid alone resolves the scan, exactly as it
 * always did, and the route ignores whatever sits in the host position. That is deliberate: a scan's
 * LANDED hostname can differ from the submitted one after redirects, and re-deriving it later must
 * never turn an already-shared link into a 404.
 */
export function scanPermalink(uuid: string, hostname?: string | null): string {
  const slug = scanHostSlug(hostname);
  return slug ? `/scan/${slug}/${encodeURIComponent(uuid)}` : `/scan?uuid=${encodeURIComponent(uuid)}`;
}

/**
 * A hostname reduced to a safe path segment, or null when there is nothing usable.
 *
 * Returning null rather than a placeholder matters: a link to `/scan/-/<uuid>` would be indexable,
 * keyword-free and permanent, which is worse than keeping the legacy query form for the handful of
 * scans whose target never resolved to a host.
 */
/** The hostname of a URL, for callers that hold the URL but not a parsed host. Null if unparseable. */
export function hostOf(url?: string | null): string | null {
  if (!url) return null;
  try {
    return new URL(url).hostname || null;
  } catch {
    return null;
  }
}

export function scanHostSlug(hostname?: string | null): string | null {
  if (!hostname) return null;
  const slug = hostname
    .trim()
    .toLowerCase()
    .replace(/\.$/, '')
    // Hostnames are already almost path-safe; this is a belt-and-braces pass so a malformed value
    // recorded from a redirect chain can never break the URL or inject a path segment.
    .replace(/[^a-z0-9.-]/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^[-.]+|[-.]+$/g, '');
  return slug && slug.length <= 120 ? slug : null;
}

/**
 * The scan target carried by a `/scan` link, from either the base64url `u` param or the legacy
 * plaintext `url` one. Returns null for anything that is not an http(s) URL, so a malformed or
 * hand-edited parameter cannot drive a scan.
 */
export function scanTargetFrom(params: URLSearchParams | { get(key: string): string | null }): string | null {
  const encoded = params.get('u');
  if (encoded) {
    try {
      return asHttpUrl(base64UrlDecode(encoded));
    } catch {
      return null;
    }
  }
  return asHttpUrl(params.get('url'));
}

function asHttpUrl(value: string | null): string | null {
  if (!value) return null;
  const normalized = normalizeScanTarget(value);
  if (!normalized) return null;
  try {
    const parsed = new URL(normalized);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? normalized : null;
  } catch {
    return null;
  }
}

/**
 * Turns a typed or linked scan target into an absolute URL.
 *
 * A bare domain used to be rejected outright: this helper returned null for `example.com`, so a deep
 * link silently did not auto-run, and the scan box passed the raw string to the API where it failed as
 * "Invalid URL". A domain with no scheme is a perfectly ordinary thing to type.
 *
 * DELIBERATELY DUPLICATED from `normalizeSubmittedUrl` in analysis-svc rather than imported: the
 * frontend does not depend on the backend packages (same reason the report theme and degraded-reason
 * copy are restated), and this copy is only for immediate feedback. The backend remains the authority
 * and re-normalizes every submission, so a divergence here can never widen what actually gets fetched.
 */
/**
 * Undo the common defanging conventions, and nothing else.
 *
 * Extracted so `classifySeed` in `graph-model.ts` can share these exact rules. It cannot use
 * `normalizeScanTarget`, which is about producing a SCANNABLE URL and therefore prepends a scheme -
 * that would classify every pasted hash and bare domain as a URL. Refanging is the part both need;
 * what happens afterwards is not.
 */
export function refang(raw: string): string {
  return raw
    .trim()
    .replace(/hxxps/gi, 'https')
    .replace(/hxxp/gi, 'http')
    .replace(/\[\.\]|\(\.\)|\{\.\}|\[dot\]|\(dot\)/gi, '.')
    .replace(/\[:\/\/\]|\[:\]\/\//g, '://');
}

export function normalizeScanTarget(raw: string): string | null {
  // Refang the common defanging conventions, which is how a URL usually arrives pasted.
  let s = refang(raw);
  // Matched wrappers only: a trailing bracket is meaningful in URLs like /wiki/Puma_(brand).
  for (const [open, close] of [
    ['<', '>'],
    ['(', ')'],
    ['"', '"'],
    ["'", "'"],
  ] as const) {
    while (s.length > 1 && s.startsWith(open) && s.endsWith(close)) s = s.slice(1, -1).trim();
  }
  s = s.replace(/[.,;:!?]+$/, '').trim();
  if (!s) return null;
  if (s.startsWith('//')) s = `https:${s}`;

  let schemeAssumed = false;
  if (!/^[a-z][a-z0-9+.-]*:\/\//i.test(s)) {
    const token = /^([a-z][a-z0-9+.-]*):/i.exec(s);
    // `example.com:8080` parses as scheme `example.com`, so a host-looking token before a port is read
    // as a host and a port instead.
    const hostPort =
      token && (token[1]!.includes('.') || token[1]!.toLowerCase() === 'localhost') && /^\d{1,5}(?:[/?#]|$)/.test(s.slice(token[0].length));
    if (!token || hostPort) {
      s = `https://${s}`;
      schemeAssumed = true;
    }
  }
  try {
    const u = new URL(s);
    if ((u.protocol === 'http:' || u.protocol === 'https:') && !u.hostname) return null;
    // Canonicalize only what we completed. A URL given in full is returned as written, because
    // `toString()` punycodes an IDN host and percent-encodes a unicode path - which would break the
    // report-link round-trip and change the hostname the homograph analysis reads.
    return schemeAssumed ? u.toString() : s;
  } catch {
    return null;
  }
}
